import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DeviceService } from 'src/app/services/devices/device.service';
import { getRelayDeviceImage } from 'src/app/services/devices/device-type.util';
import { HistoryAnalyticsDateRange } from '../history-analytics-date-filter/history-analytics-date-filter.component';
import { AstrolumOnOffSession, formatLumHistoryDate } from 'src/app/services/LUM/lum.helper';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-history-astrolum',
  imports: [CommonModule, TranslateModule],
  templateUrl: './history-astrolum.component.html',
  styleUrl: './history-astrolum.component.scss',
})
export class HistoryAstrolumComponent implements OnChanges {

  @Input() serial = '';
  @Input() deviceInfo: any = null;
  @Input() dateRange: HistoryAnalyticsDateRange | null = null;

  loading = false;
  private pending = 0;
  relayStat: Record<string, unknown> | null = null;


  historySessions: AstrolumOnOffSession[] = [];
  readonly pageSize = 10;
  currentPage = 1;
  updatedAt: string | null = null;

  constructor(
    private _device: DeviceService,
    private translate: TranslateService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.serial) {
      return;
    }
    if (changes['serial']) {
      this.loadData();
      return;
    }
    if (changes['dateRange']) {
      this.currentPage = 1;
    }
  }

  filteredSessions(): AstrolumOnOffSession[] {
    if (!this.dateRange) {
      return this.historySessions;
    }
    const start = new Date(this.dateRange.startDate).setHours(0, 0, 0, 0);
    const end = new Date(this.dateRange.endDate).setHours(23, 59, 59, 999);
    return this.historySessions.filter((session) =>
      session.records.some((record) => {
        const time = new Date(record.at).getTime();
        return time >= start && time <= end;
      }),
    );
  }

  pagedSessions(): AstrolumOnOffSession[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredSessions().slice(start, start + this.pageSize);
  }

  totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredSessions().length / this.pageSize));
  }

  /** La página agrupa ciclos; el texto cuenta cada registro del histórico. */
  pageSummary(): { from: number; to: number; total: number } {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    let before = 0;
    let shown = 0;
    let total = 0;

    this.filteredSessions().forEach((session, index) => {
      const count = session.records.length;
      total += count;
      if (index < start) {
        before += count;
      } else if (index < end) {
        shown += count;
      }
    });

    return { from: total ? before + 1 : 0, to: before + shown, total };
  }




  private loadData(): void {
    if (!this.serial) {
      return;
    }

    this.loading = true;
    this.currentPage = 1;
    this.pending = 2;

    this._device.getElementModule(this.serial, 'relays', '0', 'data').subscribe({
      next: (data) => {
        this.historySessions = formatLumHistoryDate(data?.data ?? []);
        console.log('historySessions', this.historySessions);
        this.finish();
      },
      error: () => {
        this.historySessions = [];
        this.finish();
      },
    });
    this._device.getModuleInfo(this.serial, 'relays', 'all&elements=true').subscribe({
      next: (data) => {
        console.log('relayStat', data);
        this.relayStat = data?.elements?.[0]?.data?.stat ?? null;
        this.finish();
      },
      error: () => {
        this.relayStat = null;
        this.finish();
      },
    });
  }

  private finish(): void {
    this.pending--;
    if (this.pending === 0) {
      this.updatedAt = new Date().toISOString();
      this.loading = false;
    }
  }

  refresh(): void {
    this.loadData();
  }

  deviceImage(): string {
    return getRelayDeviceImage(this.serial, this.deviceInfo?.sub_type);
  }


  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages() || page === this.currentPage) {
      return;
    }
    this.currentPage = page;
  }

  isClosed(session: AstrolumOnOffSession): boolean {
    return !!this.record(session, 'on') && !!this.record(session, 'off');
  }

  private record(session: AstrolumOnOffSession, state: 'on' | 'off') {
    return session.records.find((record) => record.state === state);
  }


  formatDateTime(dateString: string): string {
    if (!dateString) {
      return '-';
    }
    const date = new Date(dateString);
    const lang = this.translate.currentLang || this.translate.defaultLang || 'es';
    return date.toLocaleString(lang, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  formatDuration(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours} h ${minutes} min`;
    }
    return `${minutes} min`;
  }

  download(format: 'csv' | 'xlsx'): void {
    const sessions = this.filteredSessions();
    if (!sessions.length) {
      return;
    }

    const onLabel = this.translate.instant('on');
    const offLabel = this.translate.instant('off');
    const hoursLabel = this.translate.instant('totalHours');
    const energyLabel = this.translate.instant('energy');
    const data = sessions.map((session) => {
      const on = this.record(session, 'on');
      const off = this.record(session, 'off');
      return {
        [onLabel]: this.formatDateTime(on?.at ?? ''),
        [`${onLabel} ${hoursLabel}`]: on ? this.formatDuration(on.minutes * 60) : '-',
        [`${onLabel} ${energyLabel}`]: on ? on.energyKwh.toFixed(2) : '-',
        [offLabel]: off ? this.formatDateTime(off.at) : '-',
        [`${offLabel} ${hoursLabel}`]: off ? this.formatDuration(off.minutes * 60) : '-',
        [`${offLabel} ${energyLabel}`]: off ? off.energyKwh.toFixed(2) : '-',
        [this.translate.instant('historicsDuration')]: this.isClosed(session) ? this.formatDuration(session.durationSeconds) : '-',
        [this.translate.instant('historicsEnergy')]: this.isClosed(session) ? session.energyKwh.toFixed(2) : '-',
      };
    });
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    const activityLabel = this.translate.instant('ActivityClock').replace(/[\\/:*?"<>|]/g, '').trim();
    XLSX.utils.book_append_sheet(workbook, worksheet, activityLabel.slice(0, 31));
    XLSX.writeFile(workbook, `${this.serial}_${activityLabel}.${format}`, { bookType: format });
  }
}
